'use client'

import { useEffect } from 'react'

import { setAlgoliaInsightsUserToken } from 'lib/algolia'
import { setUserContext } from 'lib/sentry'
import { syneriseIdentifyUser } from 'lib/synerise'
import { CustomerTypes, useCustomerData } from 'modules/customer'
import { noop } from 'shared/utils/common'
import { retryPromise } from 'shared/utils/repeatLoop'

const MAX_CLARITY_RETRIES = 10
const RETRY_INTERVAL = 5000
const MAX_SYNERISE_RETRIES = 10
const SYNERISE_RETRY_INTERVAL = 5000
const MAX_ALGOLIA_RETRIES = 10
const ALGOLIA_RETRY_INTERVAL = 2000

declare global {
  interface Window {
    clarity?: (e: unknown, c: unknown) => void
  }
}

const initClarity = (user: CustomerTypes) => {
  let cancelled = false
  const check = () =>
    typeof window !== 'undefined' && window?.clarity !== undefined

  retryPromise({
    maxRetries: MAX_CLARITY_RETRIES,
    retryDelay: RETRY_INTERVAL,
    check
  })
    .then(() => {
      if (cancelled) return
      if (typeof window === 'undefined' || !window?.clarity) return

      try {
        window?.clarity?.('identify', user.id)
      } catch (error) {
        console?.error('Failed to set Clarity user:', error)
      }
    })
    .catch(noop)

  return () => {
    cancelled = true
  }
}

const initSentry = (user: CustomerTypes) => {
  try {
    setUserContext({
      id: user.id,
      email: user.email
    })
  } catch (error) {
    console.error('Failed to set Sentry user:', error)
  }
}

const initSynerise = (user: CustomerTypes) => {
  let cancelled = false

  try {
    const check = () => window?.SR !== undefined

    retryPromise({
      maxRetries: MAX_SYNERISE_RETRIES,
      retryDelay: SYNERISE_RETRY_INTERVAL,
      check
    })
      .then(() => {
        if (cancelled) return
        void syneriseIdentifyUser(user)
      })
      .catch(noop)
  } catch (error) {
    console.error('Failed to set Synerise user:', error)
  }

  return () => {
    cancelled = true
  }
}

// window.aa is installed by the Algolia GTM template, so it is usually not there yet on the
// first render. Called with no id on logout, to drop the customer identity.
const initAlgoliaInsights = (customerId?: string) => {
  let cancelled = false
  const check = () => typeof window !== 'undefined' && !!window?.aa

  retryPromise({
    maxRetries: MAX_ALGOLIA_RETRIES,
    retryDelay: ALGOLIA_RETRY_INTERVAL,
    check
  })
    .then(() => {
      if (cancelled) return
      void setAlgoliaInsightsUserToken(customerId)
    })
    .catch(noop)

  return () => {
    cancelled = true
  }
}

// it couldn't be hook, because it is used in _app and it have to be rendered inside SessionProvider
export const IdentifyUserInExternals = () => {
  const { updatedUser } = useCustomerData()

  useEffect(() => {
    // Runs for logged out visitors too, so the token is reset on logout.
    const cancelAlgolia = initAlgoliaInsights(updatedUser?.id)

    if (!updatedUser) return cancelAlgolia

    // Synchronous, nothing to cancel.
    initSentry(updatedUser)
    const cancelClarity = initClarity(updatedUser)
    const cancelSynerise = initSynerise(updatedUser)

    return () => {
      cancelAlgolia()
      cancelClarity()
      cancelSynerise()
    }
  }, [updatedUser])

  return null
}
